/**
 * @copyright
 * (c) 2026 netTrek GmbH & Co. KG
 *
 * Recursive ISO-BMFF box-tree builder for C2PA BMFF hashing. Unlike the flat
 * top-level `scanBoxes`, this descends into container boxes and records each box
 * under its full xpath (e.g. `/moov/trak/mdia/minf/stbl/stco`), plus the
 * FullBox `version`/`flags` where present — everything the C2PA `c2pa.hash.bmff`
 * exclusion qualifiers (nested paths, `subset`, `version`, `flags`, `length`)
 * need to resolve to concrete byte ranges. Ported from c2pa-rs
 * `asset_handlers/bmff_io.rs::build_bmff_tree` (Apache-2.0).
 */

import { scanBoxes, fourCC } from './parseBoxes'

/** Container boxes whose children are themselves boxes (recursed into). */
const CONTAINER_TYPES = new Set([
    'moov', 'trak', 'mdia', 'minf', 'stbl', 'moof', 'traf', 'edts', 'udta',
    'dinf', 'tref', 'treg', 'mvex', 'mfra', 'meta', 'schi'
])

/** ISO/IEC 14496-12 FullBoxes — carry a 1-byte version + 3-byte flags after the header. */
const FULL_BOX_TYPES = new Set([
    'pdin', 'mvhd', 'tkhd', 'mdhd', 'hdlr', 'nmhd', 'elng', 'stsd', 'stdp', 'stts', 'ctts', 'cslg',
    'stss', 'stsh', 'elst', 'dref', 'stsz', 'stz2', 'stsc', 'stco', 'co64', 'padb', 'subs',
    'saiz', 'saio', 'mehd', 'trex', 'mfhd', 'tfhd', 'trun', 'tfra', 'mfro', 'tfdt', 'leva', 'trep',
    'assp', 'sbgp', 'sgpd', 'csgp', 'cprt', 'tsel', 'kind', 'meta', 'xml ', 'bxml', 'iloc', 'pitm',
    'ipro', 'infe', 'iinf', 'iref', 'ipma', 'schm', 'fiin', 'fpar', 'fecr', 'gitn', 'fire', 'stri',
    'stsg', 'stvi', 'csch', 'sidx', 'ssix', 'prft', 'srpp', 'vmhd', 'smhd', 'srat', 'chnl', 'dmix',
    'txtC', 'mime', 'uri ', 'uriI', 'hmhd', 'sthd', 'vvhd', 'medc'
])

/** A box located in the tree, keyed by its full xpath. */
export interface TreeBox {
    path: string
    /** Absolute box start (its size field). */
    start: number
    /** Total box size (header + payload). */
    size: number
    /** Absolute offset one past the box. */
    end: number
    /** FullBox version, if this box carries a version/flags header. */
    version?: number
    /** FullBox flags (24-bit), if present. */
    flags?: number
}

export interface BmffTree {
    /** Full xpath → boxes at that path (a path can repeat, e.g. multiple `trak`). */
    boxesByPath: Map<string, TreeBox[]>
    /** Absolute offsets of the top-level boxes, in file order. */
    topLevelOffsets: number[]
}

/** ISO 14496-12 §8.11.1 QuickTime exception: a `meta` whose first child is `hdlr` omits the FullBox header. */
function metaLacksFullboxHeader (u8: Uint8Array, payloadStart: number): boolean {
    return payloadStart + 8 <= u8.byteLength && fourCC(u8, payloadStart + 4) === 'hdlr'
}

function readFlags (u8: Uint8Array, at: number): number {
    return (u8[at] << 16) | (u8[at + 1] << 8) | u8[at + 2]
}

function add (map: Map<string, TreeBox[]>, tb: TreeBox): void {
    const list = map.get(tb.path)
    if (list) list.push(tb)
    else map.set(tb.path, [tb])
}

function walk (u8: Uint8Array, start: number, end: number, parentPath: string, map: Map<string, TreeBox[]>): void {
    for (const box of scanBoxes(u8, start, end)) {
        const path = `${parentPath}/${box.type}`
        const tb: TreeBox = { path, start: box.start, size: box.size, end: box.end }
        let childStart = box.payloadStart
        const isFull = FULL_BOX_TYPES.has(box.type)
        const isContainer = CONTAINER_TYPES.has(box.type)

        if (box.type !== 'uuid' && isFull) {
            // meta may lack the version/flags header (QuickTime); otherwise read it.
            if (box.type === 'meta' && metaLacksFullboxHeader(u8, box.payloadStart)) {
                // no version/flags; children start at payloadStart
            } else if (box.payloadStart + 4 <= box.end) {
                tb.version = u8[box.payloadStart]
                tb.flags = readFlags(u8, box.payloadStart + 1)
                childStart = box.payloadStart + 4
            }
        }
        add(map, tb)

        if (isContainer && box.type !== 'uuid') {
            walk(u8, childStart, box.end, path, map)
        }
    }
}

/** Builds the recursive box tree of `u8` (the whole asset). */
export function buildBmffTree (u8: Uint8Array): BmffTree {
    const boxesByPath = new Map<string, TreeBox[]>()
    walk(u8, 0, u8.byteLength, '', boxesByPath)
    const topLevelOffsets = scanBoxes(u8).map(b => b.start)
    return { boxesByPath, topLevelOffsets }
}
